// [PURPOSE]: Support & Feedback section for Settings tab
// [WHY]: Users need bug reporting, self-service FAQ, and developer contact info
// [INTEGRATION]: Rendered inside CollapsibleSection in Settings tab (App.jsx Section 9)
// [CREATED]: v1.1.0 — Support & Feedback system

import React, { useState } from 'react';
import { Bug, MessageSquare, ChevronDown, ChevronRight, Heart, Mail, ExternalLink, Shield, FileText } from 'lucide-react';
import BugReportDialog from './BugReportDialog';
import { APP_VERSION } from '../constants/version';

// [PURPOSE]: FAQ items — self-service answers since dev can't access user data
const FAQ_ITEMS = [
  {
    question: 'My character data is gone!',
    answer: 'Your character data is stored in your browser\'s local storage — it\'s not saved to any server. ' +
      'If you cleared your browser data, used incognito mode, or switched browsers, your data may be lost. ' +
      'To prevent this, regularly use the Export button (in the character header) to save a backup JSON file. ' +
      'You can import it anytime to restore your character.',
  },
  {
    question: 'How do I transfer characters between devices?',
    answer: 'On your current device, tap the Export button in the character header to download a JSON file. ' +
      'Transfer that file to your other device (email, cloud drive, etc.), then open the app and use Import to load it. ' +
      'Character data does not sync between devices automatically — only your account and premium status sync via the cloud.',
  },
  {
    question: 'The app won\'t load or shows a white screen',
    answer: 'Try these steps in order:\n' +
      '1. Hard refresh the page (Ctrl+Shift+R or Cmd+Shift+R)\n' +
      '2. Clear your browser cache for this site\n' +
      '3. Try opening in an incognito/private window\n' +
      '4. If you see an error screen with a "Copy Error" button, copy it and include it in a bug report — that helps us fix the issue.',
  },
  {
    question: 'My stats seem wrong',
    answer: 'Several things can affect your stats: active conditions (exhaustion, poisoned, etc.), ' +
      'equipped items and their magical bonuses, feats, and class features. ' +
      'Check the Conditions tracker on your Character tab, verify your equipment is properly attuned if required, ' +
      'and make sure all your feats are correctly selected. The app calculates everything automatically based on your setup.',
  },
  {
    question: 'How do I reset everything?',
    answer: 'Go to Settings > Rules & System > scroll to the Debug section. ' +
      'You\'ll find a "Nuclear Reset" option that clears all data and returns the app to its fresh state. ' +
      'Warning: this is irreversible — export your characters first if you want to keep them!',
  },
  {
    question: 'Is my data backed up?',
    answer: 'No — your character data lives only in your browser\'s local storage. There is no automatic cloud backup. ' +
      'Always export your characters before clearing browser data, switching devices, or uninstalling the app. ' +
      'Your account info and premium status ARE stored in the cloud and will restore when you log in.',
  },
  {
    question: 'Premium features not showing after purchase',
    answer: 'Go to Settings > your Account panel and tap the "Refresh" button to re-check your premium status. ' +
      'If that doesn\'t work, try signing out and back in. ' +
      'Your purchase is tied to your account and works across all platforms — it just sometimes takes a moment to sync.',
  },
];

/**
 * [PURPOSE]: Self-contained support section with FAQ, bug report, and contact links
 * [PATTERN]: Follows ContentWorkshopSection — standalone component with local dialog state
 */
const SupportSection = () => {
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugReportCategory, setBugReportCategory] = useState('bug');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  // [HANDLER]: Open bug report dialog with specific category
  const openReport = (cat) => {
    setBugReportCategory(cat);
    setShowBugReport(true);
  };

  // [HANDLER]: Toggle FAQ accordion item
  const toggleFAQ = (index) => {
    setExpandedFAQ(prev => prev === index ? null : index);
  };

  return (
    <>
      {/* ─── Quick Actions ─── */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => openReport('bug')}
          className="flex items-center justify-center gap-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-red-500 transition-all text-center"
        >
          <Bug size={18} className="text-red-400" />
          <span className="font-medium text-white text-sm">Report a Bug</span>
        </button>
        <button
          onClick={() => openReport('feedback')}
          className="flex items-center justify-center gap-2 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-blue-500 transition-all text-center"
        >
          <MessageSquare size={18} className="text-blue-400" />
          <span className="font-medium text-white text-sm">Send Feedback</span>
        </button>
      </div>

      {/* ─── FAQ / Troubleshooting ─── */}
      <div>
        <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-2">
          Frequently Asked Questions
        </h4>
        <div className="space-y-1">
          {FAQ_ITEMS.map((item, index) => (
            <div key={index} className="bg-gray-700/50 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-gray-700 transition-colors"
              >
                {expandedFAQ === index
                  ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                  : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                }
                <span className="text-sm text-gray-200 font-medium">{item.question}</span>
              </button>
              {expandedFAQ === index && (
                <div className="px-3 pb-3 pl-9">
                  <p className="text-sm text-gray-400 whitespace-pre-line leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ─── Links & Info ─── */}
      <div className="space-y-3">
        {/* Support the Developer */}
        <a
          href="https://ko-fi.com/desertrain"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-900/30 to-red-900/30 rounded-lg border border-pink-800/50 hover:border-pink-600 transition-all group"
        >
          <Heart size={20} className="text-pink-400 group-hover:text-pink-300" />
          <div className="flex-1">
            <div className="text-sm font-medium text-pink-300 group-hover:text-pink-200">
              Support the Developer
            </div>
            <div className="text-xs text-gray-500">
              Buy me a coffee on Ko-fi
            </div>
          </div>
          <ExternalLink size={14} className="text-gray-600 group-hover:text-gray-400" />
        </a>

        {/* Contact & Legal */}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
          <a
            href="mailto:silosupport@gmail.com"
            className="flex items-center gap-1 hover:text-gray-300 transition-colors"
          >
            <Mail size={12} />
            Contact Support
          </a>
          <span className="text-gray-700">|</span>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-gray-300 transition-colors"
          >
            <Shield size={12} />
            Privacy Policy
          </a>
          <span className="text-gray-700">|</span>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-gray-300 transition-colors"
          >
            <FileText size={12} />
            Terms of Service
          </a>
        </div>

        {/* Version */}
        <div className="text-center text-xs text-gray-600 pt-1">
          Tabletop Inventory Manager v{APP_VERSION}
        </div>
      </div>

      {/* ─── Bug Report Dialog ─── */}
      <BugReportDialog
        isOpen={showBugReport}
        onClose={() => setShowBugReport(false)}
        initialCategory={bugReportCategory}
      />
    </>
  );
};

export default SupportSection;
